/* Home / dashboard landing view. */
import { currentUser } from "../auth.js";
import { loadQuestions } from "../data.js";
import {
  allProgress, listBookmarks, getMeta, getStreakFreezeState,
  getFlashcardSeenIds,
} from "../db.js";
import { dueNow } from "../srs.js";
import { navigate } from "../router.js";
import {
  loadSubscription, isPro, FREE_PRACTICE_LIMIT, practiceRemaining,
  FREE_MULTIPLAYER_LIMIT, multiplayerRemaining, hasHitMultiplayerLimit,
  FREE_FLASHCARD_LIMIT, flashcardRemaining, hasHitFlashcardLimit,
} from "../subscription.js";
import { lockBadge } from "../paywall.js";
// Exam history was previously rendered here too; the canonical surface
// is now the Stats / Dashboard page (/dashboard), so the home view no
// longer imports the renderer or the toggle wirer.
import { primeAvatar, getAvatar, DEFAULT_AVATAR } from "../avatar.js";
import {
  subscribePublicRooms, subscribeRoom,
  getActiveRoomCode, clearActiveRoomCode,
} from "../multiplayer.js";
import { isInstallable, triggerInstall } from "../install-prompt.js";
import { getDailySet, countAnsweredToday, DAILY_SET_SIZE } from "../daily-question.js";
import { loaderHtml } from "../loader.js";
import { renderHeatmap } from "../heatmap.js";
import { reconcileReminderState, dismissReminderPrompt, enablePush } from "../push.js";
import { shouldShowWelcomeDiagnostic } from "./welcome-diagnostic.js";
import { weakestSubjects } from "../picker.js";
import { getDailyTerm, getDailyFact, primeDailyContent } from "../daily-content.js";
import { STREAK_MILESTONES } from "../achievements.js";

// Live open-matches subscription for the multiplayer tile badge. Torn down
// on hashchange so it doesn't keep ticking while the user is on other views.
let _mpOpenUnsub = null;
let _mpHashGuard = null;
let _mpActiveRoomUnsub = null;
function cleanupOpenMatchesBadge() {
  if (_mpOpenUnsub)        { _mpOpenUnsub();        _mpOpenUnsub = null; }
  if (_mpHashGuard)        { window.removeEventListener("hashchange", _mpHashGuard); _mpHashGuard = null; }
  if (_mpActiveRoomUnsub)  { _mpActiveRoomUnsub();  _mpActiveRoomUnsub = null; }
}

export async function renderHome(view) {
  const user = currentUser();
  // Branded heartbeat loader while the home payload (subs + progress +
  // bookmarks + meta + flashcard counts) resolves. The home view is
  // light so this is usually <1s on warm caches.
  view.innerHTML = `
    <section class="home">
      <div class="card">${loaderHtml("Loading your home…")}</div>
    </section>`;
  // loadSubscription must resolve before getStreakFreezeState — the freeze
  // state depends on isPro(), which is sync-after-load.
  const [qs, progress, bookmarks, meta, /*sub*/, fcSeen] = await Promise.all([
    loadQuestions(), allProgress(), listBookmarks(), getMeta(),
    loadSubscription(),
    // Free-tier flashcard quota — drives the tile copy below.
    // Cheap one-doc read; Pro users still pay for it but the home page
    // only loads a few times per session, so the cost is negligible.
    getFlashcardSeenIds().catch(() => []),
    // Fetch the Firestore-backed term/fact bank in parallel so the
    // carousel uses live admin-edited content rather than the bundled
    // defaults. Silently falls back to bundled defaults on failure.
    primeDailyContent(),
  ]);
  const freezeState = await getStreakFreezeState().catch(() => null);

  // First-run diagnostic gate. Brand-new users (zero progress, no prior
  // diagnostic record) get redirected into the welcome check-up before
  // they see the dashboard. Once they complete or skip, the meta record
  // suppresses this on every future visit. The skeleton above flashes
  // briefly during this navigate — that's intentional and unavoidable
  // without restructuring boot, but the redirect is fast (no extra
  // network round-trip — meta + progress are already loaded).
  if (shouldShowWelcomeDiagnostic({ meta, progressCount: progress.length })) {
    navigate("/welcome-diagnostic");
    return;
  }

  // Seed the avatar cache off the meta doc we already fetched so the hero
  // and top chip stay in sync without an extra round-trip.
  await primeAvatar(meta);
  const avatar = getAvatar() || DEFAULT_AVATAR;
  const byId = new Map(qs.map((q) => [q.id, q]));
  const pro = isPro();
  const needsPro = pro ? "" : " locked";
  const lock = pro ? "" : lockBadge();
  // Multiplayer is partially free: FREE_MULTIPLAYER_LIMIT races per
  // free user before the paywall kicks in. The home tile shows remaining
  // races and only locks once the user has exhausted their free allowance.
  const mpRemaining = multiplayerRemaining();
  const mpExhausted = hasHitMultiplayerLimit();
  const mpLocked = !pro && mpExhausted;
  const mpSub = pro
    ? "Live head-to-head race with a friend"
    : mpExhausted
      ? `Free limit reached (${FREE_MULTIPLAYER_LIMIT}/${FREE_MULTIPLAYER_LIMIT}), upgrade`
      : `${mpRemaining} of ${FREE_MULTIPLAYER_LIMIT} free race${mpRemaining === 1 ? "" : "s"} left`;
  // Free-tier: count distinct questions attempted, not answer events.
  const attemptedUniq = new Set(progress.map((p) => p.qid)).size;
  const freeRemaining = practiceRemaining(attemptedUniq);
  const freeExhausted = !pro && freeRemaining <= 0;
  // Free-tier flashcard quota — mirrors the practice quota.
  const fcSeenCount   = Array.isArray(fcSeen) ? fcSeen.length : 0;
  const fcRemaining   = flashcardRemaining(fcSeenCount);
  const fcExhausted   = !pro && hasHitFlashcardLimit(fcSeenCount);
  const due = dueNow(progress).length;
  const answered = progress.length;
  const correct = progress.filter((p) => p.correct).length;
  const acc = answered ? Math.round((correct / answered) * 100) : 0;

  // Today's reps — count distinct progress records updated since local
  // midnight. Drives the "Today" stat-grid tile so users see a fresh
  // counter every day they open the app, instead of a static lifetime
  // total that never changes by much. Sources include practice / review
  // / flashcards / daily-5 / exam / multiplayer — anything that fires
  // saveProgress lands here.
  const todayStartMs = (() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d.getTime();
  })();
  const todayCount = progress.filter((p) => {
    const ts = Number(p.updated || p.lastReview || 0);
    return ts >= todayStartMs;
  }).length;

  const name = (user.displayName || user.email || "friend").split("@")[0];
  const greeting = pickGreeting({ due, answered, acc, streak: meta.streak || 0 });


  // ───────────────────────────────────────────────────────────────────
  // Build the "do this next" carousel as a MIX of two slide kinds:
  //
  //   · Coach slides — personalised observations about the user.
  //     Streak watch, weakest subject, week-over-week trending, etc.
  //     Drawn from existing progress/meta data; CTAs are subtle
  //     ("See breakdown", "Drill it") rather than mode-switching.
  //
  //   · Today slides — content of the day (term of the day, did-you-
  //     know fact). Same content for every signed-in user that day,
  //     selected by day-of-epoch from app/js/daily-content.js. No CTA
  //     — the slide IS the content; user reads + swipes.
  //
  // Previously every slide pointed at a tile destination (Review,
  // Daily, Practice, Multiplayer), which made the carousel feel like
  // a redundant copy of the tile grid below. The new mix gives the
  // carousel its own job: "what to KNOW" (coach + today) instead of
  // "what to DO" (tiles).
  //
  // Free-tier upgrade nudge is still pinned as the first slide when
  // applicable — it doesn't have a tile equivalent and converts well.
  // ───────────────────────────────────────────────────────────────────

  // Top weak subject — only flagged when there's enough data to trust
  // (≥10 answered in that subject) and the accuracy is genuinely poor
  // (<75%). Otherwise no "weak" card is shown.
  const _hfWeakInfo = (() => {
    const names = weakestSubjects(progress, 1);
    if (!names.length) return null;
    const name = names[0];
    let total = 0, correct = 0;
    for (const p of progress) {
      const subs = String(p.subject || "").split("|");
      if (!subs.includes(name)) continue;
      total++;
      if (p.correct) correct++;
    }
    if (total < 10) return null;
    const acc = Math.round((correct / total) * 100);
    if (acc >= 75) return null;
    return { name, acc, total };
  })();

  // "Strong week" highlight: the subject with the best accuracy over the
  // last 7 days (≥10 cards touched, ≥80% correct on the last attempt).
  //
  // Deliberately NOT a week-over-week delta: progress records are
  // per-card snapshots (lastReview + last-answer `correct`, overwritten
  // on every attempt), so a card re-attempted this week vacates any
  // "prior week" bucket entirely. Two windows built from snapshots are
  // disjoint card sets, and a "X% → Y%" delta computed from them is an
  // artifact of WHICH cards were re-touched (re-drilling last week's
  // failures fabricates a huge jump), not a real accuracy change. A
  // single-window stat is the honest version of this slide.
  const _hfStrongWeekInfo = (() => {
    const now = Date.now();
    const WK = 7 * 86_400_000;
    const agg = new Map();
    for (const p of progress) {
      const t = Number(p.lastReview || 0);
      if (!t || now - t >= WK) continue;
      for (const s of String(p.subject || "").split("|").filter(Boolean)) {
        const e = agg.get(s) || { total: 0, ok: 0 };
        e.total++;
        if (p.correct) e.ok++;
        agg.set(s, e);
      }
    }
    let best = null;
    for (const [name, e] of agg) {
      if (e.total < 10) continue;
      const acc = e.ok / e.total;
      if (acc < 0.80) continue;
      if (!best || acc > best.accRaw || (acc === best.accRaw && e.total > best.total)) {
        best = { name, accRaw: acc, acc: Math.round(acc * 100), total: e.total };
      }
    }
    return best;
  })();

  // Streak milestone: how many days until the next streak badge? Derived
  // from the canonical STREAK_MILESTONES ladder in achievements.js — the
  // same list that actually unlocks the badges — so the slide can never
  // promise a milestone that doesn't exist or skip one that does.
  const _hfStreakInfo = (() => {
    const s = Number(meta.streak || 0);
    if (s < 3) return null;       // not interesting yet
    const next = STREAK_MILESTONES.find((m) => m.days > s);
    if (!next) return { streak: s, daysTo: 0, milestone: "all milestones cleared" };
    return { streak: s, daysTo: next.days - s, milestone: `the "${next.title}" badge` };
  })();

  const _hfCandidates = [];

  // 1) Free-tier upgrade nudge — always first when applicable.
  if (!pro && freeExhausted) {
    _hfCandidates.push({
      kind: "upgrade",
      id: "upgrade", color: "amber",
      icon: '<span aria-hidden="true">✨</span>',
      eyebrow: "You've used your free practice",
      title: "Unlock unlimited",
      sub: `First month $3.99 with code INTRO. ${meta.streak || 0}-day streak keeps going.`,
      href: "#/upgrade", cta: "See Pro plans",
    });
  }

  // 2) Coach: streak watch (motivation toward next badge)
  if (_hfStreakInfo) {
    _hfCandidates.push({
      kind: "coach",
      id: "coach-streak", color: "amber",
      icon: '<span aria-hidden="true">🔥</span>',
      eyebrow: "Streak watch",
      title: `Day ${_hfStreakInfo.streak} of a streak`,
      sub: _hfStreakInfo.daysTo > 0
        ? `${_hfStreakInfo.daysTo} day${_hfStreakInfo.daysTo === 1 ? "" : "s"} from ${_hfStreakInfo.milestone}. Daily 5 keeps it alive.`
        : "You've cleared every streak milestone. Just keep going for vanity points.",
      href: "#/dashboard", cta: "Streak history",
    });
  }

  // 3) Today: term of the day
  const _hfTerm = getDailyTerm();
  if (_hfTerm) {
    _hfCandidates.push({
      kind: "today",
      id: "today-term", color: "blue",
      icon: '<span aria-hidden="true">📖</span>',
      eyebrow: "Term of the day",
      title: _hfTerm.term,
      sub: _hfTerm.definition,
      href: null, cta: null,
    });
  }

  // 4) Coach: weakest subject
  if (_hfWeakInfo) {
    _hfCandidates.push({
      kind: "coach",
      id: "coach-weak", color: "coral",
      icon: '<span aria-hidden="true">🎯</span>',
      eyebrow: "Weak spot",
      title: `${_hfWeakInfo.name} · ${_hfWeakInfo.acc}%`,
      sub: `${_hfWeakInfo.total} attempts. ~15 min of drilling moves the needle.`,
      href: `#/practice?subject=${encodeURIComponent(_hfWeakInfo.name)}`,
      cta: `Drill ${_hfWeakInfo.name}`,
    });
  }

  // 5) Today: did-you-know fact
  const _hfFact = getDailyFact();
  if (_hfFact) {
    _hfCandidates.push({
      kind: "today",
      id: "today-fact", color: "purple",
      icon: '<span aria-hidden="true">💡</span>',
      eyebrow: "Did you know?",
      title: _hfFact.title,
      sub: _hfFact.body,
      href: null, cta: null,
    });
  }

  // 6) Coach: strong week — positive reinforcement on the subject the
  //    user is nailing right now.
  if (_hfStrongWeekInfo) {
    _hfCandidates.push({
      kind: "coach",
      id: "coach-strong-week", color: "teal",
      icon: '<span aria-hidden="true">📈</span>',
      eyebrow: "Strong week",
      title: `${_hfStrongWeekInfo.name} · ${_hfStrongWeekInfo.acc}%`,
      sub: `${_hfStrongWeekInfo.total} cards in the last 7 days. Keep it rolling.`,
      href: "#/dashboard", cta: "See breakdown",
    });
  }

  // 7) Defensive fallback — should only fire on a brand-new account
  //    with no progress, no streak, and no daily-content available.
  //    (In practice term + fact always populate, so we get ≥2 slides.)
  if (!_hfCandidates.length) {
    _hfCandidates.push({
      kind: "coach",
      id: "explore", color: "teal",
      icon: '<span aria-hidden="true">📖</span>',
      eyebrow: "Get started",
      title: "Your first session awaits",
      sub: "Pick a mode below or start the Daily 5 — your stats will fill in as you go.",
      href: "#/practice", cta: "Start practice",
    });
  }

  const _hfHeroCards = _hfCandidates.slice(0, 5);

  // ───────────────────────────────────────────────────────────────────
  // Primary tile selection — picks ONE "do this now" tile that spans
  // two grid columns and gets the bento treatment (tinted background,
  // larger title, primary CTA button). The remaining standard tiles
  // render in the regular grid, omitting the one promoted to primary.
  //
  // Priority ladder:
  //   1. UPGRADE   — free user, hit the 50-question quota
  //   2. REVIEW    — Pro + due > 0 (any backlog wins this slot)
  //   3. RACE      — caught-up users (no review backlog). Drives the
  //                  multiplayer feature for everyone who isn't in
  //                  remedial mode. Free users with races remaining
  //                  also get pushed here.
  //   4. PRACTICE  — brand-new account fallback (< 10 attempted, no
  //                  data yet to suggest anything else).
  //
  // The `excludes` field on the resulting object tells the grid below
  // which standard tile id to skip rendering (so we don't show the
  // same destination twice).
  // ───────────────────────────────────────────────────────────────────
  const _hfPrimary = (() => {
    // 1) Free-tier exhausted upgrade prompt
    if (!pro && freeExhausted) {
      return {
        id: "primary-upgrade", excludes: null,
        accent: "amber",
        href: "#/upgrade",
        icon: "✨",
        eyebrow: "Unlock unlimited",
        title: "You've used your 50 free questions",
        sub: `First month $3.99 with code INTRO. Keep your ${meta.streak || 0}-day streak going.`,
        cta: "See Pro plans",
      };
    }
    // 2) Pro user with a review backlog
    if (pro && due > 0) {
      const big = due >= 20;
      return {
        id: "primary-review", excludes: "review",
        accent: "teal",
        href: "#/review",
        icon: "🔁",
        eyebrow: big ? "Most due · big backlog" : "Most due",
        title: `Review queue · ${due.toLocaleString()} card${due === 1 ? "" : "s"} due`,
        sub: big
          ? "Spaced repetition · pick a chunk, ~10 min clears 60 cards."
          : `Spaced repetition · about ${Math.max(2, Math.ceil(due / 6))} min to clear.`,
        cta: "Start review",
      };
    }
    // 3) Race — the default primary for anyone caught up on review,
    //    AND for free users who haven't hit the multiplayer cap. This
    //    is the lever that drives multiplayer adoption: any user
    //    visiting the home page without a more urgent task gets a
    //    big bento card pushing them into the lobby.
    const canRace = !mpLocked;
    const hasSomeProgress = answered >= 10;
    if (canRace && (hasSomeProgress || !pro)) {
      return {
        id: "primary-race", excludes: "multiplayer",
        accent: "purple",
        href: "#/multiplayer",
        icon: "⚔️",
        eyebrow: pro ? "Live · race now" : `${mpRemaining} free race${mpRemaining === 1 ? "" : "s"} left`,
        title: pro ? "Race anyone in the lobby" : "Test your speed",
        sub: pro
          ? "Quick head-to-head — first to answer wins the round. Public matches usually start in <60s."
          : `Free users get ${FREE_MULTIPLAYER_LIMIT} races to try multiplayer. Race a friend by code or jump into the public lobby.`,
        cta: pro ? "Find a match" : `Race anyone (${mpRemaining} free)`,
      };
    }
    // 4) Practice fallback — brand new account with nothing to suggest
    return {
      id: "primary-practice", excludes: "practice",
      accent: "teal",
      href: "#/practice",
      icon: "📖",
      eyebrow: "Start here",
      title: pro ? "Start a practice session" : `Your ${freeRemaining} free questions`,
      sub: pro
        ? "Filter by species, subject, or just jump into a mixed deck. Every answer feeds the spaced-repetition system."
        : `${freeRemaining} of your ${FREE_PRACTICE_LIMIT} free questions remaining. No card required.`,
      cta: "Start practice",
    };
  })();


  view.innerHTML = `
    <section class="home">
      <div class="hero hero-stats">
        <div class="hero-row">
          <div class="hero-greeting">
            <a class="home-hero-avatar" href="#/account" title="Change your avatar" aria-label="Change your avatar">${esc(avatar)}</a>
            <div class="hero-text">
              <h1>Hi ${esc(name)} 👋</h1>
              <p class="hero-subtitle muted">${esc(greeting)}</p>
            </div>
          </div>
          ${pro
            ? `<span class="pro-chip" title="StudyVET Pro">✨ Pro</span>`
            : `<a class="pro-chip pro-chip-free" href="#/upgrade" title="Upgrade to Pro">Free · Upgrade →</a>`}
        </div>
        <!-- Stats chips row — divider above, four chips for streak,
             today, due/quota, and accuracy. Each chip surfaces a
             meaningful glanceable number that previously required
             scrolling down to the stat grid. The "due" chip is a link
             into the review queue for Pro users; Free users see a
             quota chip linking to the upgrade page instead. Streak +
             accuracy chips are conditional (hidden until there's data
             to show) so the hero doesn't look pre-populated with
             zeros on a brand-new account. -->
        <hr class="hero-divider" />
        <div class="hero-chips">
          ${(meta.streak || 0) > 0
            ? `<span class="hero-chip hero-chip-streak" title="Current daily streak">🔥 ${meta.streak}-day streak</span>`
            : ""}
          <span class="hero-chip hero-chip-today" title="Questions answered today">⚡ ${todayCount.toLocaleString()} today</span>
          ${pro
            ? (due > 0
                ? `<a class="hero-chip hero-chip-due" href="#/review" title="Open the review queue">🔁 ${due.toLocaleString()} due</a>`
                : `<span class="hero-chip hero-chip-due hero-chip-zero" title="No cards due right now">🔁 0 due</span>`)
            : `<a class="hero-chip hero-chip-quota" href="#/upgrade" title="Free practice questions remaining">🎟 ${freeRemaining} of ${FREE_PRACTICE_LIMIT} free</a>`}
          ${answered > 0
            ? `<span class="hero-chip hero-chip-accuracy" title="Lifetime accuracy">🎯 ${acc}% accuracy</span>`
            : ""}
        </div>
        ${pro ? "" : `
          <div class="home-quota ${freeExhausted ? "home-quota-done" : ""}">
            <div class="home-quota-label">
              <span><strong>${attemptedUniq}</strong> / ${FREE_PRACTICE_LIMIT} free practice questions used</span>
              <a class="link" href="#/upgrade">${freeExhausted ? "Upgrade →" : "Unlock unlimited →"}</a>
            </div>
            <div class="home-quota-bar">
              <span style="width:${Math.min(100, Math.round(attemptedUniq / FREE_PRACTICE_LIMIT * 100))}%"></span>
            </div>
          </div>`}
      </div>

      ${(() => {
        const dailySet = getDailySet(qs, DAILY_SET_SIZE);
        if (!dailySet.length) return "";
        const setIds = dailySet.map((q) => q.id);
        const done = countAnsweredToday(progress, setIds);
        const total = dailySet.length;
        // Hide the card entirely once all of today's set is answered.
        if (done >= total) return "";
        // Segmented-dots layout (Hero+Daily round 1, variant A). A row
        // of `total` small circles fills in left-to-right as the user
        // grades each daily question. Matches the dot language used in
        // the dashboard mosaic so the visual idiom is consistent
        // across the app. The CTA collapses to a single arrow on the
        // right so the card stays one row tall on desktop.
        const dotsHtml = Array.from({ length: total }, (_, i) =>
          `<span class="daily-dot${i < done ? " on" : ""}" aria-hidden="true"></span>`
        ).join("");
        const ctaLabel = done === 0 ? "Start" : "Continue";
        return `
          <a href="#/daily" class="card daily-card daily-card-dots" aria-label="${ctaLabel} daily ${total} (${done} of ${total} done)">
            <div class="daily-icon-tile" aria-hidden="true">⭐</div>
            <div class="daily-body">
              <div class="daily-row">
                <span class="daily-label">Daily ${total}</span>
                <div class="daily-dots" aria-hidden="true">${dotsHtml}</div>
                <span class="daily-count">${done} of ${total}</span>
              </div>
            </div>
            <span class="daily-cta-arrow">${ctaLabel} <span aria-hidden="true">→</span></span>
          </a>`;
      })()}

      <!-- Multiplayer "your opponent has arrived" banner. Empty until a
           host who has wandered off to /home gets a guest joining their
           room, see wireActiveMultiplayerBanner() below. -->
      <div id="mp-active-room-slot"></div>

      <div class="card-grid card-grid-bento">
        <!-- Primary "do this now" tile · spans 2 columns on desktop,
             full-width on mobile. Picked by priority ladder above. -->
        <a class="action-card action-card-primary" data-accent="${esc(_hfPrimary.accent)}" href="${esc(_hfPrimary.href)}">
          <div class="action-icon action-icon-primary">${esc(_hfPrimary.icon)}</div>
          <div class="action-primary-body">
            <div class="action-primary-eyebrow">${esc(_hfPrimary.eyebrow)}</div>
            <div class="action-primary-title">${esc(_hfPrimary.title)}</div>
            <div class="action-primary-sub">${esc(_hfPrimary.sub)}</div>
            <div class="action-primary-cta">${esc(_hfPrimary.cta)} <span aria-hidden="true">→</span></div>
          </div>
          ${_hfPrimary.excludes === "multiplayer" ? `
          <!-- The regular multiplayer tile (which normally hosts the live
               open-matches badge) is excluded when Race is promoted to
               primary — so the badge moves HERE, otherwise exactly the
               users being steered into the lobby never see the count. -->
          <span class="action-badge" id="mp-open-badge" hidden aria-label="Open matches"></span>` : ""}
        </a>
        ${_hfPrimary.excludes === "practice" ? "" : `
        <a class="action-card${freeExhausted ? " locked" : ""}" href="#/practice">
          <div class="action-icon">📖</div>
          <div>
            <div class="action-title">Practice</div>
            <div class="action-sub">${pro
              ? "Filter by species &amp; subject"
              : freeExhausted
                ? `Free limit reached (${FREE_PRACTICE_LIMIT}/${FREE_PRACTICE_LIMIT}) — upgrade`
                : `${freeRemaining} free question${freeRemaining === 1 ? "" : "s"} left`}</div>
          </div>
        </a>`}
        ${_hfPrimary.excludes === "review" ? "" : `
        <a class="action-card ${due ? "highlight" : ""}${needsPro}" href="${pro ? "#/review" : "#/upgrade"}">
          <div class="action-icon">🔁</div>
          <div>
            <div class="action-title">Review queue ${lock}</div>
            <div class="action-sub">${due ? `${due} card${due === 1 ? "" : "s"} due` : "Nothing due — good job!"}</div>
          </div>
        </a>`}
        <a class="action-card${needsPro}" href="${pro ? "#/exam" : "#/upgrade"}">
          <div class="action-icon">📝</div>
          <div>
            <div class="action-title">Mock exam ${lock}</div>
            <div class="action-sub">Custom filters, size &amp; timing</div>
          </div>
        </a>
        <a class="action-card${fcExhausted ? " locked" : ""}" href="#/flashcards">
          <div class="action-icon action-icon-svg">
            <!-- Match the bottom-nav flashcards glyph: violet duotone stacked
                 cards. Sized via the .action-icon-svg rule so the visual
                 weight matches the surrounding 30px emoji icons. -->
            <svg viewBox="0 0 24 24" fill="#8b5cf6" aria-hidden="true">
              <rect x="3.5" y="6" width="13" height="15" rx="2"
                    transform="rotate(-9 10 13.5)" opacity="0.45"/>
              <rect x="7" y="3" width="13" height="15" rx="2"/>
            </svg>
          </div>
          <div>
            <div class="action-title">Flashcards${fcExhausted ? " " + lock : ""}</div>
            <div class="action-sub">${pro
              ? "Self-grade — Again / Hard / Good / Easy"
              : fcExhausted
                ? `Free limit reached (${FREE_FLASHCARD_LIMIT}/${FREE_FLASHCARD_LIMIT}) — upgrade`
                : `${fcRemaining} of ${FREE_FLASHCARD_LIMIT} free card${fcRemaining === 1 ? "" : "s"} left`}</div>
          </div>
        </a>
        ${_hfPrimary.excludes === "multiplayer" ? "" : `
        <a class="action-card${mpLocked ? " locked" : ""}" href="${mpLocked ? "#/upgrade" : "#/multiplayer"}" id="mp-home-tile">
          <div class="action-icon">⚔️</div>
          <div>
            <div class="action-title">Multiplayer${mpLocked ? " " + lock : ""}</div>
            <div class="action-sub">${esc(mpSub)}</div>
          </div>
          <span class="action-badge" id="mp-open-badge" hidden aria-label="Open matches"></span>
        </a>`}
        <a class="action-card" href="#/dashboard">
          <div class="action-icon">📊</div>
          <div>
            <div class="action-title">Progress${pro ? "" : " " + lock}</div>
            <div class="action-sub">${pro ? "Your stats &amp; weak areas" : "Preview your stats &amp; bests"}</div>
          </div>
        </a>
        ${isInstallable() ? `
        <button type="button" class="action-card" id="install-app-card">
          <div class="action-icon">📲</div>
          <div>
            <div class="action-title">Install app</div>
            <div class="action-sub">Home-screen access, works offline</div>
          </div>
        </button>` : ""}
      </div>

      <!-- ── Swipable "what to know" carousel ────────────────────────
           Lives BELOW the tile grid so the primary CTA is what the
           user sees first. Carousel is supplementary: coach insights
           (streak watch, trending up, weakest subject) interleaved
           with "today" content (term of the day, did-you-know fact).
           Free-tier upgrade nudge still pins as first slide when
           applicable. CSS scroll-snap handles the swipe; dot-pager
           wiring at the bottom of this function syncs the active
           index. -->
      <section class="hf-hero-wrap" aria-label="Insights and today's pick">
        <div class="hf-hero-carousel" id="hf-carousel" tabindex="0" role="region" aria-roledescription="carousel">
          ${_hfHeroCards.map((c, i) => `
            <article class="hf-hero-slide${i === 0 ? " is-active" : ""}" data-card="${esc(c.id)}" data-color="${esc(c.color)}" data-kind="${esc(c.kind || "")}" aria-roledescription="slide" aria-label="${esc(c.title)}">
              <div class="hf-hero-head">
                <span class="hf-hero-icon" aria-hidden="true">${c.icon}</span>
                <span class="hf-hero-eyebrow">${esc(c.eyebrow)}</span>
                ${c.kind === "coach"  ? `<span class="hf-hero-type-tag hf-type-coach">Coach</span>` : ""}
                ${c.kind === "today"  ? `<span class="hf-hero-type-tag hf-type-today">Today</span>` : ""}
              </div>
              <h2 class="hf-hero-title">${esc(c.title)}</h2>
              <p class="hf-hero-sub">${esc(c.sub)}</p>
              ${c.href && c.cta ? `
                <div class="hf-hero-actions">
                  <a class="hf-hero-cta" href="${esc(c.href)}">${esc(c.cta)} →</a>
                </div>
              ` : ""}
            </article>
          `).join("")}
        </div>
        ${_hfHeroCards.length > 1 ? `
          <div class="hf-hero-dots" role="tablist" aria-label="Carousel pages">
            ${_hfHeroCards.map((c, i) => `
              <button type="button" class="hf-dot${i === 0 ? " is-on" : ""}" data-idx="${i}" role="tab" aria-selected="${i === 0 ? "true" : "false"}" aria-label="Show: ${esc(c.title)}"></button>
            `).join("")}
          </div>` : ""}
      </section>

      <section class="card hm-card">
        <h3>Activity</h3>
        ${renderHeatmap(progress)}
      </section>

      <div class="stat-grid">
        <div class="stat stat-today${todayCount > 0 ? " is-active" : ""}" title="Questions you've answered since midnight local time. Counts practice, review, flashcards, exams, daily-5, and multiplayer.">
          <div class="stat-num">${todayCount.toLocaleString()}</div>
          <div class="stat-lbl">Today</div>
        </div>
        <div class="stat"><div class="stat-num">${answered.toLocaleString()}</div><div class="stat-lbl">Attempted</div></div>
        <div class="stat"><div class="stat-num">${acc}%</div><div class="stat-lbl">Accuracy</div></div>
        <div class="stat">
          <div class="stat-num">${meta.streak || 0}</div>
          <div class="stat-lbl">Day streak${freezeState && freezeState.isPro
            ? ` · <span class="freeze-pill" title="Pro: ${freezeState.freezes} of ${freezeState.max} streak freezes left this month — they auto-protect missed days.">❄️ ${freezeState.freezes}</span>`
            : ""}</div>
        </div>
      </div>

      ${bookmarks.length ? `
        <section class="card">
          <h3>${bookmarks.length} bookmarked question${bookmarks.length === 1 ? "" : "s"}</h3>
          <a class="btn btn-ghost" href="#/practice?bookmarks=1">Review bookmarks →</a>
        </section>` : ""}
    </section>
  `;


  // ─── Carousel wiring (Variant F hero) ──────────────────────────────
  // scroll-snap handles the actual swipe; we just sync the dots when the
  // user scrolls, and scroll the right slide into view when a dot is
  // clicked. Keyboard arrows shift the active card when the carousel is
  // focused. All inside an `if (hfCarousel)` guard so a future refactor
  // that drops the carousel won't error.
  const hfCarousel = view.querySelector("#hf-carousel");
  if (hfCarousel) {
    const hfSlides = Array.from(hfCarousel.querySelectorAll(".hf-hero-slide"));
    const hfDots   = Array.from(view.querySelectorAll(".hf-dot"));

    function hfSetActive(idx, { scroll = true } = {}) {
      if (!hfSlides.length) return;
      const clamped = Math.max(0, Math.min(hfSlides.length - 1, idx));
      hfSlides.forEach((s, i) => s.classList.toggle("is-active", i === clamped));
      hfDots.forEach((d, i) => {
        d.classList.toggle("is-on", i === clamped);
        d.setAttribute("aria-selected", i === clamped ? "true" : "false");
      });
      if (scroll && hfSlides[clamped]) {
        hfSlides[clamped].scrollIntoView({ behavior: "smooth", inline: "start", block: "nearest" });
      }
    }

    hfDots.forEach((dot) => {
      dot.addEventListener("click", () => hfSetActive(Number(dot.dataset.idx) || 0));
    });

    // Sync active dot when the user swipes (rAF-throttled to avoid
    // scroll thrash). Slide width + 12px gap is the step distance.
    let hfScrollRaf = 0;
    hfCarousel.addEventListener("scroll", () => {
      if (hfScrollRaf) return;
      hfScrollRaf = requestAnimationFrame(() => {
        hfScrollRaf = 0;
        const slideW = hfSlides[0] ? hfSlides[0].getBoundingClientRect().width : 1;
        const idx = Math.round(hfCarousel.scrollLeft / (slideW + 12));
        hfSetActive(idx, { scroll: false });
      });
    }, { passive: true });

    hfCarousel.addEventListener("keydown", (e) => {
      const cur = hfSlides.findIndex((s) => s.classList.contains("is-active"));
      if (e.key === "ArrowRight") { hfSetActive(cur + 1); e.preventDefault(); }
      if (e.key === "ArrowLeft")  { hfSetActive(cur - 1); e.preventDefault(); }
    });
  }


  // Reminder reconciliation — run AFTER the page is visible so the soft
  // prompt slides in below the fold instead of blocking initial paint.
  // Errors are non-fatal; a missing global settings doc, a denied permission,
  // or an offline state all just mean "do nothing".
  if (pro) {
    reconcileReminderState({ isPro: true })
      .then((res) => maybeShowReminderPrompt(view, res))
      .catch((err) => console.warn("[home] reminder reconcile failed:", err));
  }

  // Wire the "Install app" tile when present. Routes to the native Chrome
  // prompt or, on iOS, surfaces a Share → Add to Home Screen instruction.
  const installBtn = view.querySelector("#install-app-card");
  if (installBtn) {
    installBtn.addEventListener("click", async () => {
      installBtn.disabled = true;
      try {
        const status = await triggerInstall();
        if (status === "accepted") {
          window.toast?.("App installed!", "ok");
          installBtn.remove();
        } else if (status === "ios-instructions") {
          window.toast?.(
            "On iPhone: tap the Share icon (square with up-arrow) in Safari, then 'Add to Home Screen'.",
            "info",
          );
        } else if (status === "already-installed") {
          window.toast?.("StudyVET is already installed.", "info");
          installBtn.remove();
        } else if (status === "unsupported") {
          window.toast?.("Your browser doesn't support app install, try Chrome or Edge on mobile.", "warn");
        }
      } finally {
        installBtn.disabled = false;
      }
    });
  }

  // Live "open matches" bubble on the multiplayer tile. Now fires for Pro
  // users AND for free users with races remaining — if the user has already
  // burned their 5 free races, the tile is locked and the listener is
  // pointless, so skip in that case.
  cleanupOpenMatchesBadge();
  if (!mpLocked) {
    const badge = view.querySelector("#mp-open-badge");
    const me = currentUser();
    if (badge) {
      _mpOpenUnsub = subscribePublicRooms((rooms) => {
        // Exclude our own open rooms — we can't join them, so the number
        // would be misleading for a user currently hosting.
        const joinable = rooms.filter((r) => !me || r.hostUid !== me.uid);
        const n = joinable.length;
        if (n <= 0) {
          badge.hidden = true;
          badge.textContent = "";
          return;
        }
        badge.hidden = false;
        badge.textContent = n > 99 ? "99+" : String(n);
        badge.title = `${n} open match${n === 1 ? "" : "es"}, tap to join`;
      }, { max: 50 });
    }
    _mpHashGuard = () => {
      if (!location.hash.startsWith("#/home") && location.hash !== "" && location.hash !== "#") {
        cleanupOpenMatchesBadge();
      }
    };
    window.addEventListener("hashchange", _mpHashGuard);
  }

  // ─── Active-room banner ─────────────────────────────────────────────
  // If the user has a pending multiplayer room (e.g. they hit Create
  // then bounced back to home), subscribe to it. As soon as a guest
  // claims the slot, render an "Opponent ready" banner with a Resume
  // button. Idempotent — safe to re-call across home re-renders.
  wireActiveMultiplayerBanner(view);
}

/* ─── Active multiplayer-room banner ──────────────────────────────────
   Read the active room code from localStorage (written by createRoom /
   joinRoom / quickMatch). If present, subscribe to that room. Render a
   call-to-action whenever the room is in a state the user should act on:

     · host has waiting room with a guest who just joined  → "Opponent
       ready! Tap to start the race." (pulses to grab attention)
     · room is "playing" and we're on home for some reason → "Race in
       progress — return to it."
     · still waiting alone → quiet "Waiting for opponent…" with the
       room code so the user knows the room is live in the background

   On status finished/abandoned we clear the active code so a stale code
   doesn't haunt the home page across sessions. */
function wireActiveMultiplayerBanner(view) {
  const slot = view.querySelector("#mp-active-room-slot");
  if (!slot) return;
  const code = getActiveRoomCode();
  if (!code) { slot.innerHTML = ""; return; }
  const me = currentUser();
  if (!me) return;

  if (_mpActiveRoomUnsub) { _mpActiveRoomUnsub(); _mpActiveRoomUnsub = null; }
  _mpActiveRoomUnsub = subscribeRoom(code, (room, err) => {
    if (err || !room) {
      // Doc gone or rules-blocked — wipe the stale code so we don't loop.
      clearActiveRoomCode();
      slot.innerHTML = "";
      return;
    }
    // Match concluded — drop the code, hide the banner, stop watching.
    if (room.status === "finished" || room.status === "abandoned") {
      clearActiveRoomCode();
      slot.innerHTML = "";
      if (_mpActiveRoomUnsub) { _mpActiveRoomUnsub(); _mpActiveRoomUnsub = null; }
      return;
    }
    const isHost = room.hostUid === me.uid;
    const guestArrived = !!room.guestUid;
    const inProgress = room.status === "playing";
    let title = "", body = "", emoji = "🎮", variant = "waiting";
    if (isHost && guestArrived && room.status === "waiting") {
      // The headline scenario.
      emoji = "⚔️"; variant = "ready";
      title = `${esc(room.guestName || "Someone")} is ready to race!`;
      body  = `Tap to start your match. Question pool already locked in.`;
    } else if (inProgress) {
      emoji = "▶"; variant = "playing";
      title = "Race in progress";
      body  = "Pick up where you left off, your opponent is waiting.";
    } else if (isHost && !guestArrived && room.status === "waiting") {
      emoji = "⏳"; variant = "waiting";
      title = "Waiting for an opponent…";
      body  = `Room <code>${esc(room.code || code)}</code> is live. We'll let you know when someone joins.`;
    } else {
      // Guest in a still-waiting room is unusual — bail rather than
      // surface a confusing banner.
      slot.innerHTML = "";
      return;
    }
    slot.innerHTML = `
      <a class="card mp-active-banner mp-active-${variant}"
         href="#/multiplayer?code=${esc(room.code || code)}">
        <span class="mp-active-emoji" aria-hidden="true">${emoji}</span>
        <div class="mp-active-text">
          <div class="mp-active-title">${title}</div>
          <div class="mp-active-body muted">${body}</div>
        </div>
        <span class="mp-active-cta">Open →</span>
      </a>`;
  });
}

function esc(s) {
  return String(s ?? "").replaceAll("&","&amp;").replaceAll("<","&lt;").replaceAll(">","&gt;");
}

/* ─── Soft reminder prompt ─────────────────────────────────────────────
   When reconcileReminderState() returns { action: "prompt" }, we slot a
   small "Enable daily reminders?" banner near the top of the page. The
   user either accepts (triggers the browser permission prompt + auto-
   subscribe at admin's hour) or dismisses for a week. */
function maybeShowReminderPrompt(view, res) {
  if (!res || res.action !== "prompt") return;
  if (view.querySelector("#reminder-prompt")) return;  // already shown

  const banner = document.createElement("section");
  banner.id = "reminder-prompt";
  banner.className = "card reminder-prompt";
  banner.innerHTML = `
    <div class="reminder-prompt-row">
      <div class="reminder-prompt-text">
        <strong>🔔 Enable daily reminders?</strong>
        <span class="muted">We'll ping you at ${esc(fmtHour12(res.hour))} (your local time) when cards are due.</span>
      </div>
      <div class="reminder-prompt-actions">
        <button type="button" class="btn btn-ghost" id="reminder-not-now">Not now</button>
        <button type="button" class="btn btn-primary" id="reminder-enable">Enable</button>
      </div>
    </div>
  `;
  // Insert just below the hero so it's visible without disrupting the rest.
  // Defensive fallback chain: hero sibling → .home prepend → view prepend.
  // Previously if BOTH .hero and .home were absent the banner element was
  // created but never inserted, silently failing — we'd think we shown
  // the prompt and the dismissed flag wouldn't fire either. Now `view`
  // is the last-resort parent so we never end up with an orphan element.
  const hero = view.querySelector(".hero");
  const home = view.querySelector(".home");
  if (hero && hero.parentNode) {
    hero.parentNode.insertBefore(banner, hero.nextSibling);
  } else if (home) {
    home.prepend(banner);
  } else {
    view.prepend(banner);
  }

  banner.querySelector("#reminder-not-now").addEventListener("click", () => {
    dismissReminderPrompt();
    banner.remove();
  });
  banner.querySelector("#reminder-enable").addEventListener("click", async (e) => {
    const btn = e.currentTarget;
    btn.disabled = true; btn.textContent = "Enabling…";
    try {
      const r = await enablePush({ hour: res.hour });
      if (r.ok) {
        window.toast?.("Daily reminders enabled.", "ok");
        banner.remove();
      } else {
        const msg = {
          "permission-denied": "You denied notification permission. You can enable it later in your browser settings.",
          "no-vapid-key":      "Push isn't fully configured yet, check back later.",
          "unsupported":       "Your browser doesn't support web push.",
          "no-service-worker": "Service worker isn't ready, refresh and try again.",
          "subscribe-failed":  "Couldn't subscribe, try again or check your network.",
          "save-failed":       "Subscribed locally but couldn't save preferences.",
        }[r.reason] || "Couldn't enable reminders right now.";
        window.toast?.(msg, "warn");
        btn.disabled = false; btn.textContent = "Enable";
      }
    } catch (err) {
      console.warn("[home] enable from prompt failed:", err);
      btn.disabled = false; btn.textContent = "Enable";
    }
  });
}

function fmtHour12(h) {
  const n = Number(h) || 0;
  if (n === 0) return "12 AM";
  if (n < 12)  return `${n} AM`;
  if (n === 12) return "12 PM";
  return `${n - 12} PM`;
}

/* ─── Quirky greeting picker ────────────────────────────────────────────
   Chooses a subtitle from pools keyed to user state + time of day.
   Seed is deterministic per-day + attempts, so it stays stable across
   simple hash-navigation refreshes but rolls over as you study. */
function pickGreeting({ due, answered, acc, streak }) {
  const h = new Date().getHours();
  const timeOfDay =
    h < 5  ? "lateNight" :
    h < 12 ? "morning"   :
    h < 17 ? "afternoon" :
    h < 22 ? "evening"   : "lateNight";

  const pools = [];

  // First-timer warmth
  if (answered < 5) {
    pools.push(
      "First steps on the ward round, let's go.",
      "Welcome to the clinic. Gown's on the hook.",
      "Fresh chart, sharp stethoscope. Let's begin.",
      "Every great vet started at question #1.",
    );
  }

  // Time-of-day flavour (always eligible, weighted by having ≥1 line per slot)
  pools.push(...({
    morning: [
      "Rounds start early. Let's go.",
      "Coffee in, brains online.",
      "Morning clinic's open.",
      "The parasites are already awake. You in?",
    ],
    afternoon: [
      "Afternoon surgery block, scrubs on.",
      "Prime focus hours. Let's crush some questions.",
      "Fuelled up? The dairy cow case is waiting.",
      "Keep the momentum going.",
    ],
    evening: [
      "Evening rounds, home stretch.",
      "Wind-down study? Let's get a few in.",
      "One more patient before dinner.",
      "Twilight studying hits different.",
    ],
    lateNight: [
      "Night shift it is. Don't let the retrovirus bite.",
      "Burning the midnight formalin.",
      "Very dedicated. Hydrate between questions.",
      "The only thing awake now is you and the barn cats.",
    ],
  }[timeOfDay]));

  // Due-queue nudges
  if (due >= 20) {
    pools.push(
      `${due} cards waiting, let's whittle it down.`,
      `The review queue is giving you the eye. ${due} items deep.`,
      "That's a big stack. A little goes a long way.",
    );
  } else if (due > 0) {
    pools.push(
      `${due} due for review today.`,
      `Just ${due} card${due === 1 ? "" : "s"} to stay on top of things.`,
      "Small queue, big wins.",
    );
  } else if (answered > 20) {
    pools.push(
      "Inbox zero on reviews. Beautiful.",
      "All caught up on spaced-rep. Free practice time!",
      "No one's due. What shall we learn today?",
    );
  }

  // Streak feedback
  if (streak >= 30) {
    pools.push(`${streak}-day streak, you're a force.`, "Consistency icon status. Keep rolling.");
  } else if (streak >= 7) {
    pools.push(`${streak} days in a row. Nicely done.`, "The streak continues.");
  } else if (streak === 0 && answered > 50) {
    pools.push("Picking back up, no fanfare needed.", "Welcome back. The chart's right where you left it.");
  }

  // Accuracy-tinted nudges (only once you've got enough data)
  if (answered >= 30) {
    if (acc >= 85) {
      pools.push(
        `${acc}% accuracy, board-ready energy.`,
        "You're making this look easy.",
        "At this rate they'll name a technique after you.",
      );
    } else if (acc < 55) {
      pools.push(
        "Getting reps in is the whole game. Keep going.",
        "Wrong answers teach faster than right ones.",
        "Every miss is a future 'oh yeah, I know this one'.",
      );
    }
  }

  // Evergreen quirk — always eligible so the pool never empties
  pools.push(
    "Ready for another round?",
    "Let's see what you've got.",
    "One question at a time. Science awaits.",
    "Your patients (hypothetical) thank you.",
    "The exam trembles.",
    "Small steps, steep learning curve.",
    "You, a desk, and a whole lot of pathology.",
  );

  // Seeded by day + attempts so it's stable on refresh but shifts as you work.
  const day = Math.floor(Date.now() / 86_400_000);
  const seed = (day * 997 + answered * 13) >>> 0;
  return pools[seed % pools.length];
}
